/**
 * Система трекинга посетителей сайта
 * Собирает детальную информацию о пользователе и отправляет на сервер
 */

interface DeviceData {
  deviceModel: string;
  screenWidth: number;
  screenHeight: number;
  devicePixelRatio: number;
  isTablet: boolean;
}

interface VisitorData {
  action: string;
  data: {
    page?: string;
    page_type?: string;
    page_title?: string;
    user_agent?: string;
    referer?: string | null;
    load_time?: number;
    device_data?: DeviceData;
    element?: string;
    scroll_depth?: number;
  };
  battery_level?: number;
}

class VisitorTracker {
  private apiEndpoint: string;
  private batteryLevel: number | null = null;
  private sessionStarted: boolean = false;

  constructor() {
    this.apiEndpoint = '/api/v1/visitor-analytics/log';
    this.init();
  }

  /**
   * Инициализация трекера
   */
  private async init() {
    try {
      // Получаем заряд батареи если возможно
      await this.getBatteryLevel();

      // Логируем начальную загрузку страницы
      await this.logPageLoad();

      // Устанавливаем слушатели событий
      this.setupEventListeners();

      this.sessionStarted = true;
    } catch (error) {
      console.error('Ошибка инициализации трекера:', error);
    }
  }

  /**
   * Получение заряда батареи
   */
  private async getBatteryLevel(): Promise<void> {
    try {
      // @ts-ignore - Battery API может не быть в типах
      if ('getBattery' in navigator) {
        // @ts-ignore
        const battery = await navigator.getBattery();
        this.batteryLevel = Math.round(battery.level * 100);

        // Обновляем заряд при изменении
        battery.addEventListener('levelchange', () => {
          this.batteryLevel = Math.round(battery.level * 100);
        });
      }
    } catch (error) {
      // Батарея недоступна, это нормально
      this.batteryLevel = null;
    }
  }

  /**
   * Получение детальной информации об устройстве
   */
  private getDeviceData(): DeviceData {
    const userAgent = navigator.userAgent;
    let deviceModel = 'Неизвестное устройство';

    // Определение модели устройства
    if (/iPhone/.test(userAgent)) {
      // Попытка определить модель iPhone
      const match = userAgent.match(/iPhone OS ([0-9_]+)/);
      if (match) {
        const iosVersion = match[1].replace(/_/g, '.');
        deviceModel = `iPhone (iOS ${iosVersion})`;
      } else {
        deviceModel = 'iPhone';
      }
    } else if (/iPad/.test(userAgent)) {
      const match = userAgent.match(/OS ([0-9_]+)/);
      if (match) {
        const iosVersion = match[1].replace(/_/g, '.');
        deviceModel = `iPad (iPadOS ${iosVersion})`;
      } else {
        deviceModel = 'iPad';
      }
    } else if (/Macintosh/.test(userAgent)) {
      const match = userAgent.match(/Mac OS X ([0-9_]+)/);
      if (match) {
        const macVersion = match[1].replace(/_/g, '.');
        deviceModel = `Mac (macOS ${macVersion})`;
      } else {
        deviceModel = 'Mac';
      }
    } else if (/Android/.test(userAgent)) {
      // Samsung
      if (/SM-[A-Z0-9]+/.test(userAgent)) {
        const match = userAgent.match(/SM-([A-Z0-9]+)/);
        deviceModel = match ? `Samsung Galaxy ${match[1]}` : 'Samsung';
      }
      // Xiaomi
      else if (/(Mi|Redmi|POCO)\s+([A-Z0-9\s]+)/.test(userAgent)) {
        const match = userAgent.match(/(Mi|Redmi|POCO)\s+([A-Z0-9\s]+)/);
        deviceModel = match ? `Xiaomi ${match[1]} ${match[2]}`.trim() : 'Xiaomi';
      }
      // Google Pixel
      else if (/Pixel\s*([0-9a-zA-Z\s]*)/.test(userAgent)) {
        const match = userAgent.match(/Pixel\s*([0-9a-zA-Z\s]*)/);
        deviceModel = match ? `Google Pixel ${match[1]}`.trim() : 'Google Pixel';
      } else {
        const match = userAgent.match(/Android ([0-9.]+)/);
        deviceModel = match ? `Android ${match[1]}` : 'Android Device';
      }
    } else if (/Windows/.test(userAgent)) {
      const match = userAgent.match(/Windows NT ([0-9.]+)/);
      if (match) {
        const versions: Record<string, string> = {
          '10.0': 'Windows 10/11',
          '6.3': 'Windows 8.1',
          '6.2': 'Windows 8',
          '6.1': 'Windows 7',
        };
        deviceModel = versions[match[1]] || `Windows NT ${match[1]}`;
      } else {
        deviceModel = 'Windows PC';
      }
    } else if (/Linux/.test(userAgent)) {
      deviceModel = 'Linux PC';
    }

    // Определяем планшет или нет
    const isTablet = /iPad|Android(?!.*Mobile)/.test(userAgent);

    return {
      deviceModel,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      devicePixelRatio: window.devicePixelRatio || 1,
      isTablet,
    };
  }

  /**
   * Определение типа страницы
   */
  private getPageType(): string {
    const path = window.location.pathname;
    
    if (path === '/' || path === '') {
      return 'homepage';
    }
    if (path === '/login') {
      return 'login';
    }
    if (path === '/register') {
      return 'register';
    }
    if (path.startsWith('/drive')) {
      return 'drive';
    }
    if (path.startsWith('/admin')) {
      return 'admin';
    }
    if (path.startsWith('/pricing')) {
      return 'pricing';
    }
    if (path.startsWith('/contact')) {
      return 'contact';
    }
    
    return 'other';
  }

  /**
   * Логирование загрузки страницы
   */
  private async logPageLoad(): Promise<void> {
    const data: VisitorData = {
      action: 'page_load',
      data: {
        page: window.location.pathname,
        page_type: this.getPageType(),
        page_title: document.title,
        user_agent: navigator.userAgent,
        referer: document.referrer || null,
        load_time: performance.now(),
        device_data: this.getDeviceData(),
      },
    };

    if (this.batteryLevel !== null) {
      data.battery_level = this.batteryLevel;
    }

    await this.sendData(data);
  }

  /**
   * Логирование клика
   */
  private async logClick(target: HTMLElement): Promise<void> {
    const data: VisitorData = {
      action: 'click',
      data: {
        page: window.location.pathname,
        device_data: this.getDeviceData(),
      },
    };

    if (this.batteryLevel !== null) {
      data.battery_level = this.batteryLevel;
    }

    await this.sendData(data);
  }

  /**
   * Логирование скролла
   */
  private async logScroll(): Promise<void> {
    const data: VisitorData = {
      action: 'scroll',
      data: {
        page: window.location.pathname,
        device_data: this.getDeviceData(),
      },
    };

    if (this.batteryLevel !== null) {
      data.battery_level = this.batteryLevel;
    }

    await this.sendData(data);
  }

  /**
   * Установка слушателей событий
   */
  private setupEventListeners(): void {
    // Клик (только на интерактивных элементах для уменьшения шума)
    let clickTimeout: number | null = null;
    document.addEventListener('click', e => {
      if (clickTimeout) return; // Дебаунсинг
      
      const target = e.target as HTMLElement;
      // Логируем только клики на важных элементах
      const isImportant = 
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('button') ||
        target.closest('a') ||
        target.getAttribute('role') === 'button';
      
      if (!isImportant) return;
      
      clickTimeout = window.setTimeout(() => {
        clickTimeout = null;
      }, 2000);

      this.logClick(target);
    });

    // Скролл (с дебаунсингом)
    let scrollTimeout: number | null = null;
    let hasScrolled = false;
    window.addEventListener('scroll', () => {
      if (hasScrolled) return; // Логируем только первый скролл

      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        hasScrolled = true;
        this.logScroll();
      }, 1000);
    });

    // Уход со страницы
    window.addEventListener('beforeunload', () => {
      this.sendData({
        action: 'page_leave',
        data: {
          page: window.location.pathname,
          page_type: this.getPageType(),
          device_data: this.getDeviceData(),
        },
      });
    });

    // Отслеживание отправки форм (особенно важно для страницы входа/регистрации)
    document.addEventListener('submit', e => {
      const form = e.target as HTMLFormElement;
      const formAction = form.action || 'unknown';
      
      this.sendData({
        action: 'form_submit',
        data: {
          page: window.location.pathname,
          page_type: this.getPageType(),
          element: formAction,
          device_data: this.getDeviceData(),
        },
        battery_level: this.batteryLevel || undefined,
      });
    });
  }

  /**
   * Получить ID текущего пользователя из bootstrapData
   */
  private getCurrentUserId(): number | null {
    try {
      // @ts-ignore
      const bootstrapData = window.__BE_CONFIG__;
      if (bootstrapData?.user?.id) {
        return bootstrapData.user.id;
      }
    } catch (error) {
      // Пользователь не авторизован
    }
    return null;
  }

  /**
   * Получить email/имя текущего пользователя из bootstrapData
   */
  private getCurrentUsername(): string | null {
    try {
      // @ts-ignore
      const bootstrapData = window.__BE_CONFIG__;
      if (bootstrapData?.user) {
        return bootstrapData.user.email || bootstrapData.user.name || null;
      }
    } catch (error) {
      // Пользователь не авторизован
    }
    return null;
  }

  /**
   * Отправка данных на сервер
   */
  private async sendData(data: VisitorData): Promise<void> {
    try {
      // Добавляем информацию о пользователе, если авторизован
      const userId = this.getCurrentUserId();
      const username = this.getCurrentUsername();
      
      const enrichedData = {
        ...data,
        data: {
          ...data.data,
          user_id: userId,
          username: username,
        },
      };

      // Используем sendBeacon если доступен (для beforeunload)
      if (data.action === 'page_leave' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(enrichedData)], {
          type: 'application/json',
        });
        navigator.sendBeacon(this.apiEndpoint, blob);
        return;
      }

      // Обычный fetch для остальных случаев
      await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enrichedData),
        keepalive: true,
      });
    } catch (error) {
      // Игнорируем ошибки отправки, чтобы не мешать пользователю
      console.debug('Ошибка отправки аналитики:', error);
    }
  }
}

// Автоматический запуск трекера при загрузке страницы
if (typeof window !== 'undefined') {
  // Ждем полной загрузки DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new VisitorTracker();
    });
  } else {
    // DOM уже загружен
    new VisitorTracker();
  }
}

export default VisitorTracker;

